/**
 * Where every item of a scene lands on the canvas, composed through group
 * ancestors with the same maths the compositor uses (core/geometry.ts).
 */
import {
  applyToPoint,
  computeGroupFrame,
  computeItemLayout,
  groupContentMatrix,
  groupOriginShift,
  hitTestLayout,
  IDENTITY,
  invert,
  multiply,
  type ItemExtent,
  type ItemLayout,
  type Mat2D,
} from '../../core/geometry';
import type { Id, SceneItem, Vec2 } from '../../core/model';

export interface Aabb {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface PlacedItem {
  item: SceneItem;
  parentId: Id | null;
  depth: number;
  /** Maps the item's parent space (canvas or group content) to canvas pixels. */
  parentMatrix: Mat2D;
  /** Layout in parent space. */
  layout: ItemLayout;
  /** Natural content size used for the layout. */
  size: Vec2;
  /** Maps the unit square to the item box in canvas pixels. */
  worldBox: Mat2D;
  /** Box corners in canvas pixels: TL, TR, BR, BL. */
  corners: [Vec2, Vec2, Vec2, Vec2];
  /** False when the item or any ancestor group is hidden. */
  visible: boolean;
  /** True when the item or any ancestor group is locked. */
  locked: boolean;
}

export type SizeOf = (item: SceneItem) => Vec2;

/**
 * Layout of an item whose picture is displaced from its transform's box: a
 * group's drawn extent starts at its frame's `min`, so box, matrix and corners
 * move by the same origin shift the compositor applies.
 */
function shiftLayout(layout: ItemLayout, shift: Vec2): ItemLayout {
  if (shift.x === 0 && shift.y === 0) return layout;
  const move = (m: Mat2D): Mat2D => multiply({ a: 1, b: 0, c: 0, d: 1, tx: shift.x, ty: shift.y }, m);
  return {
    ...layout,
    matrix: move(layout.matrix),
    boxMatrix: move(layout.boxMatrix),
    corners: layout.corners.map((c) => ({ x: c.x + shift.x, y: c.y + shift.y })) as ItemLayout['corners'],
  };
}

/**
 * Draw order, depth first: a group is followed by its children. `sizeOf` gives
 * the natural size of sources and nested scenes; a group's size and frame are
 * derived from its children exactly as the compositor does (core/geometry.ts).
 */
export function placeItems(items: readonly SceneItem[], sizeOf: SizeOf): PlacedItem[] {
  const out: PlacedItem[] = [];
  const measure = (item: SceneItem): ItemExtent => {
    if (item.type !== 'group') return { size: sizeOf(item) };
    const frame = computeGroupFrame(item.children, measure);
    return { size: frame.size, min: frame.min };
  };
  const walk = (
    list: readonly SceneItem[],
    parentMatrix: Mat2D,
    parentId: Id | null,
    depth: number,
    visible: boolean,
    locked: boolean,
  ): void => {
    for (const item of list) {
      const extent = measure(item);
      const size = extent.size;
      const frameMin = extent.min;
      const raw = computeItemLayout(item.transform, size);
      const layout = frameMin ? shiftLayout(raw, groupOriginShift(item.transform, frameMin)) : raw;
      const worldBox = multiply(parentMatrix, layout.boxMatrix);
      const placed: PlacedItem = {
        item,
        parentId,
        depth,
        parentMatrix,
        layout,
        size,
        worldBox,
        corners: layout.corners.map((c) => applyToPoint(parentMatrix, c)) as PlacedItem['corners'],
        visible: visible && item.visible,
        locked: locked || item.locked,
      };
      out.push(placed);
      if (item.type === 'group') {
        const content = groupContentMatrix(item.transform, { min: frameMin!, size });
        walk(item.children, multiply(parentMatrix, content), item.id, depth + 1, placed.visible, placed.locked);
      }
    }
  };
  walk(items, IDENTITY, null, 0, true, false);
  return out;
}

export function aabbOf(points: readonly Vec2[]): Aabb {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

export function unionAabb(boxes: readonly Aabb[]): Aabb | null {
  if (boxes.length === 0) return null;
  return {
    minX: Math.min(...boxes.map((b) => b.minX)),
    minY: Math.min(...boxes.map((b) => b.minY)),
    maxX: Math.max(...boxes.map((b) => b.maxX)),
    maxY: Math.max(...boxes.map((b) => b.maxY)),
  };
}

export function hasArea(placed: PlacedItem): boolean {
  return Math.abs(placed.layout.boxSize.x) > 1e-6 && Math.abs(placed.layout.boxSize.y) > 1e-6;
}

/** Canvas point expressed in the item's parent space; null when the parent is collapsed to zero scale. */
export function toParentSpace(parentMatrix: Mat2D, point: Vec2): Vec2 | null {
  const inverse = invert(parentMatrix);
  return inverse ? applyToPoint(inverse, point) : null;
}

/** Converts a canvas-space displacement into the parent space (linear part only). */
export function toParentDelta(parentMatrix: Mat2D, delta: Vec2): Vec2 | null {
  const inverse = invert(parentMatrix);
  if (!inverse) return null;
  return { x: inverse.a * delta.x + inverse.c * delta.y, y: inverse.b * delta.x + inverse.d * delta.y };
}

export function hitTestPlaced(placed: PlacedItem, point: Vec2): boolean {
  const local = toParentSpace(placed.parentMatrix, point);
  return local !== null && hitTestLayout(placed.layout, local);
}

/**
 * Items under a canvas point, top-most first. From the canvas only top-level
 * items are pickable (a group is picked as a whole); nested items take part
 * only while they are already selected, so a child chosen in the sources list
 * can still be dragged.
 */
export function itemsAtPoint(placed: readonly PlacedItem[], point: Vec2, selection: ReadonlySet<Id>): PlacedItem[] {
  const hits: PlacedItem[] = [];
  for (let i = placed.length - 1; i >= 0; i--) {
    const entry = placed[i]!;
    if (!entry.visible || entry.locked || !hasArea(entry)) continue;
    if (entry.depth > 0 && !selection.has(entry.item.id)) continue;
    if (hitTestPlaced(entry, point)) hits.push(entry);
  }
  // A selected child is drawn after its group in `placed`, so it already precedes the group here.
  return hits;
}

/** Next item to select when Alt-clicking through a stack of overlapping items. */
export function cycleSelection(hits: readonly PlacedItem[], selection: ReadonlySet<Id>): Id | null {
  if (hits.length === 0) return null;
  const current = hits.findIndex((h) => selection.has(h.item.id));
  return hits[(current + 1) % hits.length]!.item.id;
}

/** Pickable items whose box lies entirely inside the marquee rectangle. */
export function itemsInRect(placed: readonly PlacedItem[], rect: Aabb): Id[] {
  const ids: Id[] = [];
  for (const entry of placed) {
    if (entry.depth > 0 || !entry.visible || entry.locked || !hasArea(entry)) continue;
    const box = aabbOf(entry.corners);
    if (box.minX >= rect.minX && box.maxX <= rect.maxX && box.minY >= rect.minY && box.maxY <= rect.maxY) ids.push(entry.item.id);
  }
  return ids;
}
